import React, { useEffect, useState } from 'react';
import { UseFormMethods } from 'react-hook-form';
import SurfaceWaiver from 'src/models/contract_types/SurfaceWaiver';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import IndexReferenceType from 'src/models/IndexReferenceType';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';

interface ISurfaceWaiverGenerationProps extends Partial<Pick<UseFormMethods, 'getValues' | 'setValue' | 'register' | 'errors'>> {
    surfaceWaiverData: SurfaceWaiver;
    measurementTypes: Array<IndexReferenceType>;
    isInHanded: boolean;
    calculateTotals: (bonusPaidTypId: number, bonusPaid: number) => string;
}

const SurfaceWaiverGeneration = (props: ISurfaceWaiverGenerationProps) => {
    const [isPerpetual, setIsPerpetual] = useState<boolean>(false);

    useEffect(() => {
        if (!!props.surfaceWaiverData) {
            calcExpirationDate();
        }
    }, [props.surfaceWaiverData]);

    const calcExpirationDate = () => {
        let refs = props.surfaceWaiverData;
        let holds = refs.surfaceWaiverHolds;

        if (holds?.length > 0) {
            const term = refs.surfaceWaiverHolds[0].primaryTerm ? refs.surfaceWaiverHolds[0].primaryTerm : undefined;
            const termUnit = refs.surfaceWaiverHolds[0].primaryTermTypeId ? refs.surfaceWaiverHolds[0].primaryTermTypeId : undefined;
            const effectiveDate = refs.effectiveDate ? new Date(refs.effectiveDate.toString()) || undefined : undefined;

            if (term && termUnit && effectiveDate && !isNaN(term) && termUnit && effectiveDate instanceof Date) {
                let termString = props.measurementTypes.find((mt) => mt.id === termUnit)?.name;
                switch (termString) {
                    case 'Years':
                        props.setValue(
                            'surfaceWaiverHolds[0].expirationDate',
                            new Date(effectiveDate.setFullYear(effectiveDate.getFullYear() + term)).toISOString().split('T')[0]
                        );
                        break;

                    case 'Months':
                        props.setValue(
                            'surfaceWaiverHolds[0].expirationDate',
                            new Date(effectiveDate.setMonth(effectiveDate.getMonth() + term)).toISOString().split('T')[0]
                        );
                        break;

                    case 'Days':
                        props.setValue(
                            'surfaceWaiverHolds[0].expirationDate',
                            new Date(effectiveDate.setDate(effectiveDate.getDate() + term)).toISOString().split('T')[0]
                        );
                        break;

                    default:
                        break;
                }
            } else {
                props.setValue('surfaceWaiverHolds[0].expirationDate', '');
            }
        }
    };

    const handleExpirationChange = () => {
        let refs = props.getValues();

        const term = refs.surfaceWaiverHolds[0].primaryTerm ? parseInt(refs.surfaceWaiverHolds[0].primaryTerm, 10) : undefined;
        const termUnit = refs.surfaceWaiverHolds[0].primaryTermTypeId ? refs.surfaceWaiverHolds[0].primaryTermTypeId : undefined;
        const effectiveDate = refs.effectiveDate ? new Date(refs.effectiveDate.toString()) || undefined : undefined;

        if (term && termUnit && effectiveDate && !isNaN(term) && termUnit && effectiveDate instanceof Date) {
            let termString = props.measurementTypes.find((mt) => mt.id === parseInt(termUnit, 10))?.name;
            switch (termString) {
                case 'Years':
                    props.setValue(
                        'surfaceWaiverHolds[0].expirationDate',
                        new Date(effectiveDate.setFullYear(effectiveDate.getFullYear() + term)).toISOString().split('T')[0]
                    );
                    break;

                case 'Months':
                    props.setValue(
                        'surfaceWaiverHolds[0].expirationDate',
                        new Date(effectiveDate.setMonth(effectiveDate.getMonth() + term)).toISOString().split('T')[0]
                    );
                    break;

                case 'Days':
                    props.setValue(
                        'surfaceWaiverHolds[0].expirationDate',
                        new Date(effectiveDate.setDate(effectiveDate.getDate() + term)).toISOString().split('T')[0]
                    );
                    break;

                default:
                    break;
            }
        } else {
            props.setValue('surfaceWaiverHolds[0].expirationDate', '');
        }
    };

    const getOptions = (type: string, item: string = '') => {
        if (props.measurementTypes?.length > 0) {
            return props.measurementTypes
                .filter((mt) => mt.description === type)
                .map((option) => {
                    if (type === 'Fee' && option.name === 'Per Item') {
                        return { value: option.id, label: `Per ${item}` };
                    }
                    return { value: option.id, label: option.name };
                });
        }
        return [];
    };

    const onUpdatePerpetual = () => {
        let ref = props.getValues();
        if (!!ref.surfaceWaiverHolds[0]) {
            setIsPerpetual(ref.surfaceWaiverHolds[0].isPerpetual === true);
        }
    };

    const handleTotalBonusPaid = () => {
        let rep = props.getValues();
        let bonusPaidTypeId = parseInt(rep.surfaceWaiverHolds[0].bonusPaidTypeId, 10);
        let bonusPaid = !isNaN(rep.surfaceWaiverHolds[0].bonusPaid) ? rep.surfaceWaiverHolds[0].bonusPaid : 0;
        let total = props.calculateTotals(bonusPaidTypeId, bonusPaid);

        props.setValue('surfaceWaiverHolds[0].totalBonusPaid', total);
    };

    return (
        <div>
            <div style={{ margin: '1rem' }}>
                <div style={{ height: '100%', width: '100%'}}>
                    <div id="formContent">
                        <div>
                            <div hidden={true}>
                                <RHFInputBox label="" name="id" register={props.register} type="number" disabled={true} errors={props.errors} />
                            </div>
                            <div className="form-row">
                                <RHFInputBox
                                    label="Surface Waiver Name"
                                    name="name"
                                    register={props.register}
                                    errors={props.errors}
                                    isRequired={true}
                                    disabled={props.isInHanded}
                                    bodyStyle={{ width: '100%', marginRight: '1rem' }}
                                    style={{width: '100%'}}
                                />
                            </div>
                            <div className="form-row">
                                <RHFInputBox
                                    label="DLC Number"
                                    name="surfaceWaiverNumber"
                                    register={props.register}
                                    errors={props.errors}
                                    disabled={true}
                                    bodyStyle={{ width: '33%' }}
                                    style={{width: '100%'}}
                                />
                                <RHFInputBox
                                    label="Contract Date"
                                    name="contractDate"
                                    register={props.register}
                                    type="date"
                                    errors={props.errors}
                                    rules={{ required: 'field required' }}
                                    isRequired={true}
                                    disabled={props.isInHanded}
                                    bodyStyle={{ width: '33%' }}
                                    style={{width: '100%'}}
                                />

                                <RHFInputBox
                                    label="Effective Date"
                                    name="effectiveDate"
                                    register={props.register}
                                    type="date"
                                    errors={props.errors}
                                    rules={{ required: 'field required' }}
                                    isRequired={true}
                                    disabled={props.isInHanded}
                                    bodyStyle={{ width: '33%', marginRight: '1rem' }}
                                    style={{width: '100%'}}
                                />
                            </div>
                            <div className="form-row">
                                <RHFSelect
                                    name="recordTypeId"
                                    label="Record Type"
                                    options={[
                                        { value: 1, label: 'Book/Volume' },
                                        { value: 2, label: 'Instrument #' },
                                    ]}
                                    register={props.register}
                                    errors={props.errors}
                                    bodyStyle={{ width: '16%' }}
                                    style={{width: '100%'}}
                                />
                                <RHFInputBox 
                                    label="Book/Volume" 
                                    name="volume" 
                                    register={props.register} 
                                    type="number" 
                                    errors={props.errors} 
                                    bodyStyle={{ width: '16%' }}
                                    style={{width: '100%'}}
                                />
                                <RHFInputBox 
                                    label="Page" 
                                    name="page" 
                                    register={props.register} 
                                    type="number" 
                                    errors={props.errors} 
                                    bodyStyle={{ width: '16%' }}
                                    style={{width: '100%'}}
                                />
                                <RHFInputBox
                                    label="Instrument Number"
                                    name="instrumentNumber"
                                    register={props.register}
                                    type="number"
                                    errors={props.errors}
                                    bodyStyle={{ width: '16%' }}
                                    style={{width: '100%'}}
                                />
                                <RHFInputBox
                                    label="Fed/State Lease #"
                                    name="fedStateLeaseNumber"
                                    register={props.register}
                                    type="number"
                                    errors={props.errors}
                                    bodyStyle={{ width: '16%' }}
                                    style={{width: '100%'}}
                                />
                                <RHFInputBox 
                                    label="Recording Date" 
                                    name="recordDate" 
                                    register={props.register} 
                                    type="date" 
                                    errors={props.errors} 
                                    bodyStyle={{ width: '16%', marginRight: '1rem' }}
                                    style={{width: '100%'}}
                                />
                            </div>

                            <div className="form-row">
                                <RHFInputBox
                                    label="Grantor AKA"
                                    name="grantorAka"
                                    register={props.register}
                                    errors={props.errors}
                                    disabled={props.isInHanded}
                                    bodyStyle={{ width: '100%', marginRight: '1rem' }}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <RHFLegend title="Surface Waiver Hold" />
                            <div hidden={true}>
                                <RHFInputBox
                                    label=""
                                    name="surfaceWaiverHolds[0].id"
                                    register={props.register}
                                    type="number"
                                    disabled={true}
                                    errors={props.errors}
                                />
                                <RHFInputBox
                                    label=""
                                    name="surfaceWaiverHolds[0].surfaceWaiverId"
                                    register={props.register}
                                    type="number"
                                    disabled={true}
                                    errors={props.errors}
                                />
                            </div>

                            <div className="form-row">
                                <RHFCheckbox
                                    name="surfaceWaiverHolds[0].isPerpetual"
                                    label="Perpetual Leasehold?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdatePerpetual()}
                                    disabled={props.isInHanded}
                                />
                            </div>
                            {!isPerpetual && (
                                <div className="form-row">
                                    <RHFInputBox
                                        label="Primary Term"
                                        name="surfaceWaiverHolds[0].primaryTerm"
                                        register={props.register}
                                        type="number"
                                        errors={props.errors}
                                        onChange={handleExpirationChange}
                                        disabled={props.isInHanded}
                                        bodyStyle={{ width: '33%' }}
                                        style={{width: '100%'}}
                                    />
                                    <RHFSelect
                                        name="surfaceWaiverHolds[0].primaryTermTypeId"
                                        label="Term Unit"
                                        options={getOptions('Time')}
                                        onChange={handleExpirationChange}
                                        register={props.register}
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                        bodyStyle={{ width: '33%' }}
                                        style={{width: '100%'}}
                                    />
                                    <RHFInputBox
                                        label="Exp. Date"
                                        name="surfaceWaiverHolds[0].expirationDate"
                                        register={props.register}
                                        type="date"
                                        errors={props.errors}
                                        disabled={true}
                                        bodyStyle={{ width: '33%', marginRight: '1rem' }}
                                        style={{width: '100%'}}
                                    />
                                </div>
                            )}

                            <div className="form-row">
                                <RHFInputBox
                                    label="Bonus Paid"
                                    name="surfaceWaiverHolds[0].bonusPaid"
                                    register={props.register}
                                    onChange={handleTotalBonusPaid}
                                    type="number"
                                    step={0.01}
                                    errors={props.errors}
                                    disabled={props.isInHanded}
                                    bodyStyle={{ width: '33%' }}
                                    style={{width: '100%'}}
                                />
                                <RHFSelect
                                    name="surfaceWaiverHolds[0].bonusPaidTypeId"
                                    label="Bonus Unit"
                                    options={getOptions('Fee', 'acre')}
                                    onChange={handleTotalBonusPaid}
                                    register={props.register}
                                    errors={props.errors}
                                    disabled={props.isInHanded}
                                    bodyStyle={{ width: '33%' }}
                                    style={{width: '100%'}}
                                />
                                <RHFInputBox
                                    label="Total Bonus Paid"
                                    name="surfaceWaiverHolds[0].totalBonusPaid"
                                    register={props.register}
                                    type="number"
                                    step={0.01}
                                    errors={props.errors}
                                    disabled={true}
                                    bodyStyle={{ width: '33%', marginRight: '1rem' }}
                                    style={{width: '100%'}}
                                />
                            </div>

                            <div className="form-row">
                                <RHFInputBox
                                    label="Additional Consideration"
                                    name="surfaceWaiverHolds[0].additionalConsideration"
                                    register={props.register}
                                    type="number"
                                    step={0.01}
                                    errors={props.errors}
                                    bodyStyle={{ width: '20%' }}
                                    style={{ width: '100%' }}
                                    disabled={props.isInHanded}
                                />
                                <RHFInputBox
                                    label="Description"
                                    name="surfaceWaiverHolds[0].description"
                                    register={props.register}
                                    errors={props.errors}
                                    disabled={props.isInHanded}
                                    bodyStyle={{ width: '100%', marginRight: '1rem' }}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SurfaceWaiverGeneration;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/redesign/SurfaceWaiverComponents/SurfaceWaiverGeneration.tsx